import { t } from '@if/core/i18n'
import { getNumberFromQueryString } from '@if/core/utils'
import { Button, Paper } from '@mui/material'
import { DataGrid, GridFooter, GridFooterContainer, GridPaginationModel } from '@mui/x-data-grid'
import { useInteracWireHistory } from '@src/api/interac-history-api'
import { EInteracWireStatus, InteracWireHistoryFiltersType } from '@src/types/interac'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDeletedInteracWireColumns } from './DeletedInteracWire-hook'
import InteracHistoryFilters, { InteracHistoryManualFiltersType } from './InteracHistoryFilters'
import { useRecordedInteracWireColumns } from './RecordedInteracWire-hook'

const InteracHistoryList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterValues = useMemo<InteracWireHistoryFiltersType>(() => {
    const status = searchParams.get('status') as EInteracWireStatus.Matched | EInteracWireStatus.Discarded
    return {
      limit: getNumberFromQueryString(searchParams, 'limit', 50),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
      status: status === EInteracWireStatus.Discarded ? EInteracWireStatus.Discarded : EInteracWireStatus.Matched,
      clientNameContains: searchParams.get('clientNameContains') || null,
      email: searchParams.get('email') || null,
    }
  }, [searchParams])

  const updatePartialFilterValues = useCallback(
    (newValues: Partial<InteracWireHistoryFiltersType>) => {
      setSearchParams((prev) => {
        const updatedSearchParams = new URLSearchParams(prev)
        Object.entries(newValues).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            updatedSearchParams.set(key, value.toString())
          } else {
            updatedSearchParams.delete(key)
          }
        })
        return updatedSearchParams
      })
    },
    [setSearchParams],
  )

  const handlePageSizeChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.pageSize !== filterValues.limit) {
        updatePartialFilterValues({ limit: model.pageSize, offset: 0 })
      }
    },
    [filterValues.limit, updatePartialFilterValues],
  )

  const handleOnPageChange = useCallback(
    (direction: number) => {
      const newOffset = direction === 0 ? filterValues.offset - filterValues.limit : filterValues.offset + filterValues.limit
      updatePartialFilterValues({ offset: newOffset })
    },
    [filterValues.offset, filterValues.limit, updatePartialFilterValues],
  )

  const onFiltersChange = useCallback(
    (newFilters: Partial<InteracHistoryManualFiltersType>) => updatePartialFilterValues(newFilters),
    [updatePartialFilterValues],
  )

  const recordedColumns = useRecordedInteracWireColumns()
  const deletedColumns = useDeletedInteracWireColumns()
  const columns = filterValues.status === EInteracWireStatus.Discarded ? deletedColumns : recordedColumns

  const [data, isPending] = useInteracWireHistory(filterValues)

  const customFooter = () => (
    <GridFooterContainer>
      <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
      <div style={{ display: 'flex' }}>
        <Button onClick={() => handleOnPageChange(0)} disabled={filterValues.offset === 0}>
          {t('common.previous')}
        </Button>
        <Button onClick={() => handleOnPageChange(1)} disabled={data.length < filterValues.limit}>
          {t('common.next')}
        </Button>
      </div>
    </GridFooterContainer>
  )

  return (
    <Paper>
      <InteracHistoryFilters defaultValues={filterValues} onFiltersChange={onFiltersChange} />
      <DataGrid
        disableColumnFilter
        disableRowSelectionOnClick
        rows={data}
        columns={columns}
        paginationModel={{ pageSize: filterValues.limit, page: filterValues.offset / filterValues.limit }}
        onPaginationModelChange={handlePageSizeChange}
        loading={isPending}
        paginationMode="server"
        rowCount={data.length}
        slots={{
          footer: customFooter,
        }}
        slotProps={{
          pagination: {
            labelDisplayedRows: () =>
              t('common.customDisplayedRowsTextFooter.displayRange', {
                defaultValue: '',
                from: filterValues.offset + 1,
                to: filterValues.offset + data.length,
              }),
          },
        }}
      />
    </Paper>
  )
}

export default InteracHistoryList
